<template>
   <div class="contact-page-bg">
      <div class="container py-4">
         <div class="row justify-content-center">
            <div class="col-xl-8 col-lg-9 col-md-11 col-12">

               <div class="contact-card">
                  <!-- Header -->
                  <div class="contact-header text-center">
                     <img src="@/assets/img/vertex_logo.png" alt="Vertex Cyber Security" class="header-logo" />
                     <h2 class="header-title">Get in Touch</h2>
                     <p class="header-subtitle">Select the services you're interested in and we'll get back to you
                        within 2 business days.</p>
                  </div>

                  <div class="contact-body">
                     <form ref="contactFormRef" @submit.prevent="validateForm()">

                        <!-- Section: Contact Details -->
                        <div class="form-section">
                           <div class="section-label">
                              <i class="fa-solid fa-user"></i>
                              <span>Contact Details</span>
                           </div>

                           <div class="form-floating mb-3">
                              <input type="text" id="organisation" v-model.trim="lead.organisation"
                                 @input="handleOrgInput" @blur="v$.lead.organisation.$touch" class="form-control"
                                 placeholder="Organisation Name" />
                              <label for="organisation">Organisation Name <span class="req">*</span></label>
                              <span class="form-text text-danger error-msg" v-if="v$.lead.organisation.$error">
                                 <span v-if="v$.lead.organisation.required && v$.lead.organisation.required.$invalid">Organisation Name is required.</span>
                                 <span v-else-if="v$.lead.organisation.validOrg && v$.lead.organisation.validOrg.$invalid">Special characters (&lt;, &gt;, &quot;, `, \) are not allowed.</span>
                              </span>
                           </div>

                           <div class="mb-3">
                              <VueMultiselect v-model="lead.selectedCountry" :options="countryOptions" :multiple="false"
                                 :close-on-select="true" :clear-on-select="false" :preserve-search="true"
                                 placeholder="Select a country" label="name" track-by="code"
                                 :preselect-first="true">
                                 <template #option="{ option }">
                                    <div class="option-with-flag">
                                       <span class="flag">{{ option.flag }}</span>&nbsp;
                                       <span>{{ option.name }}</span>
                                    </div>
                                 </template>
                                 <template #singleLabel="{ option }">
                                    <div class="option-with-flag">
                                       <span class="flag">{{ option.flag }}</span>&nbsp;
                                       <span>{{ option.name }}</span>
                                    </div>
                                 </template>
                              </VueMultiselect>
                           </div>

                           <div class="row g-3 mb-3">
                              <div class="col-md-6">
                                 <div class="form-floating">
                                    <input type="text" id="firstName" v-model.trim="lead.firstName"
                                       @blur="v$.lead.firstName.$touch" class="form-control"
                                       placeholder="First Name" />
                                    <label for="firstName">First Name <span class="req">*</span></label>
                                    <span class="form-text text-danger error-msg" v-if="v$.lead.firstName.$error">
                                       First Name is required.
                                    </span>
                                 </div>
                              </div>
                              <div class="col-md-6">
                                 <div class="form-floating">
                                    <input type="text" id="lastName" v-model.trim="lead.lastName"
                                       @blur="v$.lead.lastName.$touch" class="form-control"
                                       placeholder="Last Name" />
                                    <label for="lastName">Last Name <span class="req">*</span></label>
                                    <span class="form-text text-danger error-msg" v-if="v$.lead.lastName.$error">
                                       Last Name is required.
                                    </span>
                                 </div>
                              </div>
                           </div>

                           <div class="row g-3 mb-3">
                              <div class="col-md-6">
                                 <div class="form-floating">
                                    <input type="email" id="email" v-model.trim="lead.email"
                                       @blur="v$.lead.email.$touch" class="form-control" placeholder="Email" />
                                    <label for="email">Email <span class="req">*</span></label>
                                    <span class="form-text text-danger error-msg"
                                       v-if="v$.lead.email.$error && v$.lead.email.required.$invalid">
                                       Email is required.
                                    </span>
                                    <span class="form-text text-danger error-msg"
                                       v-if="v$.lead.email.$error && v$.lead.email.email.$invalid">
                                       Must be a valid email.
                                    </span>
                                 </div>
                              </div>
                              <div class="col-md-6">
                                 <vue-tel-input ref="vueTelInputRef" v-model.trim="lead.contactNumber"
                                    v-bind="vueTel.props" mode="international"></vue-tel-input>
                              </div>
                           </div>

                           <div class="row g-3 mb-3 align-items-center">
                              <div class="col-md-6">
                                 <div class="form-floating">
                                    <input type="text" id="abn" v-model.trim="lead.abn"
                                       @blur="v$.lead.abn.$touch" class="form-control" placeholder="ABN" />
                                    <label for="abn">ABN</label>
                                    <span class="form-text text-danger error-msg" v-if="v$.lead.abn.$error">
                                       Invalid ABN format. Must have 11 digits.
                                    </span>
                                 </div>
                              </div>
                              <div class="col-md-6">
                                 <div class="form-check">
                                    <input class="form-check-input" type="checkbox" :value="true" id="isCharity"
                                       v-model.trim="lead.isCharity">
                                    <label class="form-check-label pt-1" for="isCharity">
                                       <strong>Is a registered charity?</strong>
                                    </label>
                                 </div>
                              </div>
                           </div>
                        </div>

                        <!-- Section: Select Services -->
                        <div class="form-section">
                           <div class="section-label">
                              <i class="fa-solid fa-shield-halved"></i>
                              <span>I'm interested in... <span class="req">*</span></span>
                           </div>
                           <p class="section-hint">Please choose at least one service.</p>

                           <div v-if="isLoadingProducts" class="text-center py-4">
                              <div class="spinner-border spinner-border-sm text-info" role="status"></div>
                              <span class="ms-2 text-muted">Loading services...</span>
                           </div>

                           <div v-else class="product-grid">
                              <div v-for="product in productList" :key="product.id"
                                 class="product-card"
                                 :class="{ 'selected': isProductSelected(product) }"
                                 @click="toggleProduct(product)">
                                 <div class="product-icon">
                                    <i :class="getProductIcon(product.display_name)"></i>
                                 </div>
                                 <div class="product-name">{{ product.display_name }}</div>
                                 <div class="product-check">
                                    <i v-if="isProductSelected(product)" class="fa-solid fa-circle-check"></i>
                                    <i v-else class="fa-regular fa-circle"></i>
                                 </div>
                              </div>
                           </div>

                           <span class="form-text text-danger error-msg" v-if="showProductError">
                              Please choose at least one service.
                           </span>
                        </div>

                        <!-- Section: Additional Details -->
                        <div class="form-section">
                           <div class="section-label">
                              <i class="fa-solid fa-message"></i>
                              <span>Additional Details</span>
                           </div>

                           <div class="form-floating mb-3">
                              <textarea class="form-control" id="queryDetails" v-model.trim="lead.queryDetails"
                                 placeholder="Query Details" style="height: 120px"></textarea>
                              <label for="queryDetails">Query Details</label>
                           </div>

                           <div class="form-floating mb-3 w-100">
                              <select class="form-select" id="marketingChannel" v-model="lead.marketingChannelId">
                                 <option selected disabled value="">Please choose...</option>
                                 <option v-for="mc in marketingChannels" :value="mc.id" :key="mc.id">
                                    {{ mc.display_name }}
                                 </option>
                              </select>
                              <label for="marketingChannel">(Optional) How did you find Vertex?</label>
                           </div>

                           <div class="form-check mb-3">
                              <input class="form-check-input" type="checkbox" :value="true" id="isScheduleMeeting"
                                 v-model="lead.isScheduleMeeting">
                              <label class="form-check-label pt-1" for="isScheduleMeeting">
                                 <strong>Book a meeting with our team to discuss your request.</strong>
                              </label>
                           </div>
                        </div>

                        <!-- Submit -->
                        <div class="text-center pb-4">
                           <p v-if="formSubmitAttempted && !isFormValid" class="form-text text-danger mb-2">
                              Please review and fill up all required fields.
                           </p>
                           <button type="submit" class="btn btn-submit" :disabled="isProcessing">
                              <span v-if="isProcessing">
                                 <i class="fa-solid fa-circle-notch fa-spin me-1"></i> Submitting...
                              </span>
                              <span v-else>
                                 <i class="fa-solid fa-paper-plane me-1"></i> Send Inquiry
                              </span>
                           </button>
                        </div>

                     </form>
                  </div>

                  <!-- Footer -->
                  <div class="contact-footer text-center">
                     <p>Give us a call today on <strong>1300 2CYBER (1300 229 237)</strong></p>
                  </div>
               </div>

            </div>
         </div>
      </div>
   </div>

   <!-- Loading overlay -->
   <div v-if="isProcessing" class="loading-overlay">
      <div class="loading-card">
         <div class="spinner-border text-info mb-3" role="status"></div>
         <div><strong>Submitting your inquiry</strong></div>
         <div class="text-muted">Please wait...</div>
      </div>
   </div>
</template>

<script>
import moment from "moment";
import { useVuelidate } from '@vuelidate/core'
import { required, email, helpers } from '@vuelidate/validators'
import { VueTelInput } from 'vue-tel-input'
import 'vue-tel-input/dist/vue-tel-input.css'
import VueMultiselect from 'vue-multiselect'
import 'vue-multiselect/dist/vue-multiselect.css'
import "@/assets/scss/vue-multiselect.scss";
import { countries } from 'country-data';

const abnFormat = helpers.regex(/^(\d[ ]*?){11}$/)
const orgNameValidator = (value) => !value || !/[<>"\\`]/.test(value)

// Map product display names to Font Awesome icons
const PRODUCT_ICON_MAP = {
   'penetration test': 'fa-solid fa-magnifying-glass-location',
   'web': 'fa-solid fa-globe',
   'app': 'fa-solid fa-mobile-screen-button',
   'infrastructure': 'fa-solid fa-server',
   'cyber security audit': 'fa-solid fa-clipboard-check',
   'iso27001': 'fa-solid fa-certificate',
   'iso 27001': 'fa-solid fa-certificate',
   'soc2': 'fa-solid fa-file-shield',
   'soc 2': 'fa-solid fa-file-shield',
   'cyber training': 'fa-solid fa-chalkboard-user',
   'log monitoring': 'fa-solid fa-chart-line',
   'incident response': 'fa-solid fa-triangle-exclamation',
   'phishing': 'fa-solid fa-fish',
   'other': 'fa-solid fa-ellipsis',
}

function initialState_lead(preselectedCountry) {
   return {
      organisation: '',
      firstName: '',
      lastName: '',
      email: '',
      contactNumber: '',
      abn: '',
      isCharity: false,
      selectedCountry: preselectedCountry,
      selectedProducts: [],
      queryDetails: '',
      marketingChannelId: '',
      isScheduleMeeting: false,
   }
}

export default {
   name: 'LeadFormContactPage',

   components: {
      VueTelInput,
      VueMultiselect,
   },

   setup() {
      return { v$: useVuelidate() }
   },

   data() {
      const countryOptions = countries.all.map(country => ({
         code: country.alpha2,
         name: country.name,
         flag: country.emoji,
      }));
      const preselectedCountry = countryOptions.find(country => country.code === 'AU');

      return {
         currentDate: null,
         lead: initialState_lead(preselectedCountry),
         preselectedCountry: preselectedCountry,
         countryOptions: countryOptions,
         productList: [],
         marketingChannels: [],
         isLoadingProducts: false,
         isProcessing: false,
         isFormValid: true,
         formSubmitAttempted: false,
         showProductError: false,
         vueTel: {
            props: {
               preferredCountries: ['AU', 'US'],
               defaultCountry: "AU",
               validCharactersOnly: true,
               dropdownOptions: {
                  showDialCodeInList: true,
                  showFlags: true,
                  showSearchBox: true,
               },
               inputOptions: {
                  autocomplete: "on",
                  showDialCode: true,
                  placeholder: "Contact Number",
                  styleClasses: "form-control",
               },
            }
         },
      }
   },

   validations() {
      return {
         lead: {
            organisation: { required, validOrg: orgNameValidator },
            firstName: { required },
            lastName: { required },
            email: { required, email },
            abn: { abnFormat },
         },
      }
   },

   mounted() {
      if (window.fbq) {
         window.fbq('track', 'PageView');
      }
   },

   created() {
      this.init()
   },

   methods: {
      init() {
         this.currentDate = moment(new Date()).format("DD-MMM-YYYY");
         this.getProductList()
         this.getMarketingChannelList()
      },

      resetForm() {
         this.v$.$reset()
         Object.assign(this.lead, initialState_lead(this.preselectedCountry));
         this.showProductError = false
         this.formSubmitAttempted = false
         this.setDefaultCountryCode()
      },

      setDefaultCountryCode() {
         if (this.$refs.vueTelInputRef) {
            this.$refs.vueTelInputRef.activeCountryCode = "AU"
         }
         this.lead.contactNumber = "+61"
      },

      // Product card selection
      toggleProduct(product) {
         const idx = this.lead.selectedProducts.findIndex(p => p.id === product.id)
         if (idx > -1) {
            this.lead.selectedProducts.splice(idx, 1)
         } else {
            this.lead.selectedProducts.push(product)
         }
         if (this.lead.selectedProducts.length > 0) {
            this.showProductError = false
         }
      },

      isProductSelected(product) {
         return this.lead.selectedProducts.some(p => p.id === product.id)
      },

      getProductIcon(displayName) {
         const name = (displayName || '').toLowerCase()
         for (const [key, icon] of Object.entries(PRODUCT_ICON_MAP)) {
            if (name.includes(key)) return icon
         }
         return 'fa-solid fa-shield-halved'
      },

      async validateForm() {
         this.formSubmitAttempted = true
         const isFormCorrect = await this.v$.$validate()

         if (this.lead.selectedProducts.length === 0) {
            this.showProductError = true
         }

         if (!isFormCorrect || this.lead.selectedProducts.length === 0) {
            this.isFormValid = false

            this.$nextTick(() => {
               const firstError = document.querySelector('.error-msg')
               if (firstError) {
                  const rect = firstError.parentNode.getBoundingClientRect()
                  window.scrollTo(
                     rect.left + document.documentElement.scrollLeft,
                     rect.top + document.documentElement.scrollTop - 20
                  )
               }
            })
         } else {
            this.isFormValid = true
            this.createLead()
         }
      },

      handleOrgInput() {
         if (this.lead.organisation) {
            this.lead.organisation = this.lead.organisation.replace(/[<>"\\`]/g, '');
         }
      },

      async createLead() {
         this.isProcessing = true;

         if (this.lead.organisation) {
            this.lead.organisation = this.lead.organisation.replace(/[<>"\\`]/g, '').trim();
         }
         if (this.lead.contactNumber.trim().length < 5) {
            this.lead.contactNumber = '';
         }

         // Build product interest string for the note
         const productNames = this.lead.selectedProducts.map(p => p.display_name).join(', ')

         let noteDetails = ''
         if (productNames) {
            noteDetails += 'Interested in: ' + productNames
         }
         if (this.lead.queryDetails) {
            noteDetails += (noteDetails ? '\n\n' : '') + this.lead.queryDetails
         }

         let lead = {
            organisation_name: this.lead.organisation,
            first_name: this.lead.firstName,
            last_name: this.lead.lastName,
            email: this.lead.email,
            phone_number: this.lead.contactNumber,
            abn: this.lead.abn,
            is_charity: this.lead.isCharity,
            marketing_channel_id: this.lead.marketingChannelId,
            country_code: this.lead.selectedCountry ? this.lead.selectedCountry.code : 'AU',
            lead_notes: [{
               note_type: "initial_note_website",
               note_details: noteDetails
            }],
         };

         // Attach selected products
         lead.products = [];
         this.lead.selectedProducts.forEach((p) => {
            lead.products.push({
               product_id: p.id.toString()
            });
         });

         try {
            const response = await this.$axios_internal.post("website/leads", lead);

            if (response.data.success) {
               this.resetForm();

               if (this.lead.isScheduleMeeting) {
                  this.openCalendar()
               } else {
                  this.$router.push({ name: 'ThankYouPageContactPage' });
               }
            } else {
               this.$toast.error(response.data.message);
            }
         } catch (error) {
            this.$toast.error(error.response?.data?.message || 'An error occurred. Please try again.');
         } finally {
            this.isProcessing = false;
         }
      },

      openCalendar() {
         const calendarUrl = process.env.VUE_APP_GOOGLE_CALENDAR_LINK;
         if (calendarUrl) {
            window.location.href = calendarUrl;
         } else {
            // Fallback to thank you page if calendar link not configured
            this.$router.push({ name: 'ThankYouPageContactPage' });
         }
      },

      async getProductList() {
         this.isLoadingProducts = true
         try {
            const response = await this.$axios_internal.get("website/categories?name=Products")
            this.productList = response.data.category.subcategories
         } catch (error) {
            console.error('Error loading products:', error)
         } finally {
            this.isLoadingProducts = false
         }
      },

      async getMarketingChannelList() {
         try {
            const response = await this.$axios_internal.get("website/categories?name=MarketingChannel")
            this.marketingChannels = response.data.category.subcategories
         } catch (error) {
            console.error('Error loading marketing channels:', error)
         }
      },
   },
};
</script>

<style scoped>
/* ========== PAGE BACKGROUND ========== */
.contact-page-bg {
   min-height: 100vh;
   background: linear-gradient(135deg, #0c2d3f 0%, #00a0c1 50%, #0c2d3f 100%);
   background-attachment: fixed;
   padding: 1rem 0;
}

/* ========== CARD CONTAINER ========== */
.contact-card {
   background: #ffffff;
   border-radius: 24px;
   box-shadow: 0 20px 60px rgba(0, 0, 0, 0.15);
   overflow: hidden;
   margin: 1rem 0;
}

/* ========== HEADER ========== */
.contact-header {
   background: linear-gradient(135deg, #0c2d3f 0%, #1a4a5e 100%);
   padding: 2.5rem 2rem 2rem;
   color: #ffffff;
}

.header-logo {
   max-width: 200px;
   margin-bottom: 1.5rem;
   filter: brightness(0) invert(1);
}

.header-title {
   font-size: 1.75rem;
   font-weight: 700;
   margin-bottom: 0.5rem;
   letter-spacing: -0.02em;
}

.header-subtitle {
   font-size: 0.95rem;
   opacity: 0.85;
   margin-bottom: 0;
   max-width: 500px;
   margin-left: auto;
   margin-right: auto;
   line-height: 1.5;
}

/* ========== BODY ========== */
.contact-body {
   padding: 2rem 2.5rem;
}

@media (max-width: 576px) {
   .contact-body {
      padding: 1.5rem 1rem;
   }
}

/* ========== FORM SECTIONS ========== */
.form-section {
   margin-bottom: 2rem;
   padding-bottom: 1.5rem;
   border-bottom: 1px solid #eef2f5;
}

.form-section:last-of-type {
   border-bottom: none;
   margin-bottom: 1rem;
}

.section-label {
   display: flex;
   align-items: center;
   gap: 0.5rem;
   font-size: 1.1rem;
   font-weight: 700;
   color: #0c2d3f;
   margin-bottom: 1rem;
}

.section-label i {
   color: #00a0c1;
   font-size: 1.1rem;
}

.section-hint {
   font-size: 0.85rem;
   color: #6c757d;
   margin-bottom: 1rem;
   margin-top: -0.5rem;
}

.req {
   color: #dc3545;
}

/* ========== PRODUCT CARDS GRID ========== */
.product-grid {
   display: grid;
   grid-template-columns: repeat(3, 1fr);
   gap: 0.75rem;
}

@media (max-width: 768px) {
   .product-grid {
      grid-template-columns: repeat(2, 1fr);
   }
}

@media (max-width: 480px) {
   .product-grid {
      grid-template-columns: 1fr;
   }
}

.product-card {
   display: flex;
   align-items: center;
   gap: 0.75rem;
   padding: 0.85rem 1rem;
   border: 2px solid #e8ecef;
   border-radius: 12px;
   cursor: pointer;
   transition: all 0.2s ease;
   background: #fafbfc;
   user-select: none;
   position: relative;
}

.product-card:hover {
   border-color: #00a0c1;
   background: #f0fafd;
   transform: translateY(-1px);
   box-shadow: 0 4px 12px rgba(0, 160, 193, 0.1);
}

.product-card.selected {
   border-color: #00a0c1;
   background: linear-gradient(135deg, #e8f7fa 0%, #f0fafd 100%);
   box-shadow: 0 4px 16px rgba(0, 160, 193, 0.15);
}

.product-icon {
   width: 36px;
   height: 36px;
   border-radius: 10px;
   display: flex;
   align-items: center;
   justify-content: center;
   font-size: 1rem;
   flex-shrink: 0;
   background: linear-gradient(135deg, #00a0c1 0%, #0c2d3f 100%);
   color: #ffffff;
   transition: all 0.2s ease;
}

.product-card.selected .product-icon {
   background: linear-gradient(135deg, #00a0c1 0%, #007a94 100%);
   box-shadow: 0 2px 8px rgba(0, 160, 193, 0.3);
}

.product-name {
   flex: 1;
   font-size: 0.85rem;
   font-weight: 600;
   color: #2c3e50;
   line-height: 1.3;
}

.product-check {
   font-size: 1.2rem;
   flex-shrink: 0;
   color: #ced4da;
   transition: all 0.2s ease;
}

.product-card.selected .product-check {
   color: #00a0c1;
}

/* ========== SUBMIT BUTTON ========== */
.btn-submit {
   background: linear-gradient(135deg, #00a0c1 0%, #007a94 100%);
   color: #ffffff;
   border: none;
   padding: 0.85rem 3rem;
   font-size: 1rem;
   font-weight: 600;
   border-radius: 50px !important;
   transition: all 0.3s ease;
   letter-spacing: 0.02em;
}

.btn-submit:hover:not(:disabled) {
   background: linear-gradient(135deg, #007a94 0%, #005f73 100%);
   color: #ffffff;
   transform: translateY(-2px);
   box-shadow: 0 8px 20px rgba(0, 160, 193, 0.3);
}

.btn-submit:disabled {
   background: #6c8a94;
   color: #d0d0d0;
   cursor: not-allowed;
}

/* ========== FOOTER ========== */
.contact-footer {
   background: #f8fafb;
   border-top: 1px solid #eef2f5;
   padding: 1.25rem 2rem;
}

.contact-footer p {
   margin: 0;
   font-size: 0.9rem;
   color: #435666;
}

.contact-footer strong {
   color: #00a0c1;
}

/* ========== LOADING OVERLAY ========== */
.loading-overlay {
   position: fixed;
   top: 0;
   left: 0;
   right: 0;
   bottom: 0;
   z-index: 999;
   background-color: rgba(0, 0, 0, 0.4);
   display: flex;
   justify-content: center;
   align-items: center;
   backdrop-filter: blur(2px);
}

.loading-card {
   background: #ffffff;
   border-radius: 16px;
   padding: 2rem 3rem;
   text-align: center;
   box-shadow: 0 20px 40px rgba(0, 0, 0, 0.2);
}

/* ========== FORM OVERRIDES ========== */
.form-control,
.form-select {
   border-radius: 10px;
   border: 1px solid #D9D9D9;
}

.form-control:focus,
.form-select:focus {
   border-color: #00a0c1;
   box-shadow: 0 0 0 0.2rem rgba(0, 160, 193, 0.15);
}

.error-msg {
   font-size: 0.8rem;
}

/* ========== MULTISELECT OVERRIDES ========== */
::v-deep .multiselect__option--highlight {
   background: #00a0c1 !important;
   color: #fff !important;
}

::v-deep .multiselect__option--highlight::after {
   background: #00a0c1 !important;
   color: #fff !important;
}
</style>
